import * as React from "react";

import { PayPeriodApprovalPanel } from "@/components/PayPeriodApprovalPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatHours } from "@/lib/hoursMath";
import { getDatesInPeriod } from "@/lib/timecardStats";
import { fmtPunchTime } from "@/lib/time";
import { useTimeCardViewModel } from "@/pages/dashboard/employee/useTimeCardViewModel";

export function TimeCardView() {
  const {
    payWeeksData,
    payWeeksLoading,
    timecardPayWeek,
    setTimecardPayWeek,
    timecardEntries,
    timecardLoading,
    expandedDays,
    toggleDay,
    timecardStats,
    timecardPunchesByDay,
    selectedTimecardWeek,
  } = useTimeCardViewModel();

  if (!payWeeksData) return null;

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
              {payWeeksData && (
                <Card className="min-w-0 overflow-hidden">
                  <CardHeader>
                    <CardTitle>Time Card</CardTitle>
                    <CardDescription>
                      Select a pay week to view daily punches and hours for the period and the pay-period totals. Punches are listed per day; hours stay 0 for exempt / no paid time. Corrections are submitted via the Field Notes tab.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 min-w-0 max-w-full overflow-hidden">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="grid gap-1 min-w-[220px]">
                        <Label className="text-xs text-muted-foreground">Pay Week</Label>
                        <Select value={timecardPayWeek || "custom"} onValueChange={(v) => {
                          if (v === "custom") {
                            setTimecardPayWeek("");
                          } else {
                            // value = period from_date (unique; pay_week 02 can appear twice)
                            setTimecardPayWeek(v);
                          }
                        }}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select pay week" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Select Pay Week</SelectItem>
                            {(payWeeksData?.pay_weeks ?? []).map((w: any) => (
                              <SelectItem key={w.from_date} value={w.from_date}>{w.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {payWeeksLoading && <div className="text-[10px] text-muted-foreground">Loading pay weeks...</div>}
                      </div>
                      {timecardLoading && <div className="text-sm text-muted-foreground">Loading timecard...</div>}
                    </div>

                    {/* Daily timecard rows for the selected pay period.
                        Lists every day in the period (even 0 hours for exempt/no activity).
                        Shows day-of-week + date. Expandable to cost code breakdown. */}
                    {timecardPayWeek && selectedTimecardWeek && (
                      <div>
                        <div className="text-sm font-medium mb-2">Daily hours for {selectedTimecardWeek.label}</div>
                        <div className="overflow-auto border rounded">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left p-2">Day</th>
                                <th className="text-left p-2">Date</th>
                                <th className="text-right p-2">Hours</th>
                                <th className="w-6 p-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {getDatesInPeriod(selectedTimecardWeek.from_date, selectedTimecardWeek.to_date).map(day => {
                                const dayData = timecardStats.byDay[day] || {total: 0, byCustomer: {}};
                                const isExpanded = expandedDays.has(day);
                                // Parse YYYY-MM-DD as local date (no Z/UTC) so the weekday label matches
                                // the intended calendar day from the pay period (e.g. 2026-06-29 is Monday).
                                const [y, m, d] = day.split('-').map(Number);
                                const dateObj = new Date(y, m - 1, d);
                                const dayName = dateObj.toLocaleDateString(undefined, { weekday: 'short' });
                                const hasBreakdown = Object.keys(dayData.byCustomer || {}).some(c => Object.keys((dayData.byCustomer[c] || {}).byCostCode || {}).length > 0);
                                const dayPunches = timecardPunchesByDay[day] || [];
                                const expandable = hasBreakdown || dayPunches.length > 0;
                                return (
                                  <React.Fragment key={day}>
                                    <tr
                                      className={`border-b last:border-0 ${expandable ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                                      onClick={expandable ? () => toggleDay(day) : undefined}
                                    >
                                      <td className="p-2">{dayName}</td>
                                      <td className="p-2 font-mono">{day}</td>
                                      <td className="p-2 text-right">{formatHours(dayData.total)}</td>
                                      <td className="p-2 text-center text-xs">{expandable ? (isExpanded ? '−' : '+') : ''}</td>
                                    </tr>
                                    {isExpanded && expandable && (
                                      <tr>
                                        <td colSpan={4} className="p-0 bg-muted/10">
                                          <div className="pl-8 pr-2 py-1 text-xs">
                                            {dayPunches.map((entry) => (
                                              <div key={entry.id} className="flex justify-between gap-2 py-0.5">
                                                <span>
                                                  In {fmtPunchTime(entry.recorded_time, entry.tz_name).primary}
                                                  {" · Out "}
                                                  {fmtPunchTime(entry.clock_out_time, entry.tz_name, entry.clock_out_tz_name).primary}
                                                  {" · "}
                                                  {entry.customer_name || entry.site_name || "Unspecified"}
                                                  {" · "}
                                                  {entry.cost_code_name || "Unspecified"}
                                                </span>
                                                <span className="shrink-0 tabular-nums">{formatHours(entry.duration)}</span>
                                              </div>
                                            ))}
                                            {hasBreakdown && Object.entries(dayData.byCustomer || {}).map(([cust, custData]: [string, any]) => (
                                              <div key={cust} className="mb-1">
                                                <div className="font-medium text-muted-foreground">{cust} — {formatHours(custData.total || 0)} h</div>
                                                {Object.entries(custData.byCostCode || {}).map(([cc, h]: [string, any]) => (
                                                  <div key={cc} className="flex justify-between pl-4 py-0.5">
                                                    <span>  {cc}</span>
                                                    <span>{formatHours(h || 0)}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Pay period totals - Regular, OT1, OT2, Unp */}
                    {timecardPayWeek && (
                      <div>
                        <div className="text-sm font-medium mb-2">Pay period totals</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="rounded border p-3">
                            <div className="text-xs text-muted-foreground">Regular</div>
                            <div className="text-2xl font-semibold tabular-nums">
                              {formatHours(timecardStats.totals.regular)}
                            </div>
                          </div>
                          <div className="rounded border p-3">
                            <div className="text-xs text-muted-foreground">OT1 (weekly &gt;40h)</div>
                            <div className="text-2xl font-semibold tabular-nums">
                              {formatHours(timecardStats.totals.ot1)}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Regular = first 40 hours per Sunday–Saturday workweek. OT1 = hours after 40 in that week.
                          A bi-weekly pay period has two independent weeks. See docs/OVERTIME.md.
                        </div>
                      </div>
                    )}

                    {timecardPayWeek && selectedTimecardWeek && (
                      <PayPeriodApprovalPanel
                        periodFrom={selectedTimecardWeek.from_date}
                        periodTo={selectedTimecardWeek.to_date}
                        payWeek={selectedTimecardWeek.pay_week}
                        regularHours={timecardStats.totals.regular}
                        otHours={timecardStats.totals.ot1}
                      />
                    )}

                    {timecardPayWeek && selectedTimecardWeek && !timecardLoading && timecardEntries.length === 0 && (
                      <div className="text-sm text-muted-foreground">No hours recorded for the selected pay period.</div>
                    )}
                  </CardContent>
                </Card>
              )}

    </div>
  );
}
